import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Button,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    InputAdornment,
    Collapse,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ContentCopy as CopyIcon,
    VolumeOff as MuteIcon,
    VolumeUp as UnmuteIcon,
    Security as SecurityIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Settings as SettingsIcon,
    Wifi as WifiIcon,
    WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import { useToast } from '../toast/useToast';
import { stavSpojeniSia } from './iotStav';
import { POVOLIT_PRIDANI_SIA } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import {
    useSiaAccountsByObjektQuery,
    useSiaPreviewAccountIdQuery,
    useCreateSiaAccountMutation,
    useDeleteSiaAccountMutation,
    useGetSiaPasswordMutation,
    useRegenerateSiaPasswordMutation,
    useSiaZonesByAccountQuery,
    useMuteSiaZoneMutation,
    useUnmuteSiaZoneMutation,
    useSiaEventsByAccountQuery,
} from '../../api/JsdhNotifikaceService/service/SiaMutations';
import type {
    SiaAccount,
    SiaZone,
    SiaEvent,
    SiaPanelType,
    SiaEncryptionType,
    CreateSiaAccountRequest,
} from '../../api/JsdhNotifikaceService/generated';

interface EpsEzsSectionProps {
    objektId: number;
}

/** Nápověda k náhledu Account ID (BE posílá stejnou větu bez diakritiky). */
export const TEXT_NAHODNY_KOD = 'X bude nahrazeno náhodným kódem. Max. 16 znaků.';

const PANEL_TYPES: { value: SiaPanelType; label: string }[] = [
    { value: 'JA80', label: 'Jablotron JA-80' },
    { value: 'JA100', label: 'Jablotron JA-100' },
    { value: 'JA100PLUS', label: 'Jablotron JA-100+' },
    { value: 'GENERIC', label: 'Ostatní' },
];

const ENCRYPTION_TYPES: { value: SiaEncryptionType; label: string }[] = [
    { value: 'NONE', label: 'Bez šifrování' },
    { value: 'AES128', label: 'AES-128' },
    { value: 'AES256', label: 'AES-256' },
];

const EpsEzsSection: React.FC<EpsEzsSectionProps> = ({ objektId }) => {
    const { showToast } = useToast();
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [expandedAccountId, setExpandedAccountId] = useState<number | null>(null);
    const [muteDialogZone, setMuteDialogZone] = useState<{ zone: SiaZone; accountId: number } | null>(null);
    const [muteMinutes, setMuteMinutes] = useState(30);
    const [aktivovatBypass, setAktivovatBypass] = useState(true);
    const [deleteAccountConfirm, setDeleteAccountConfirm] = useState<SiaAccount | null>(null);

    // Form state pro novy account (accountId se generuje automaticky na serveru)
    const [newAccount, setNewAccount] = useState<CreateSiaAccountRequest>({
        objektId,
        nazev: '',
        panelType: 'JA100',
        encryptionType: 'NONE',
    });

    const { data: accounts, isLoading } = useSiaAccountsByObjektQuery(objektId);
    const { data: previewData, isLoading: isPreviewLoading } = useSiaPreviewAccountIdQuery(
        objektId,
        showAddDialog
    );
    const createMutation = useCreateSiaAccountMutation();
    const deleteMutation = useDeleteSiaAccountMutation();
    const regenerateMutation = useRegenerateSiaPasswordMutation();
    const muteMutation = useMuteSiaZoneMutation();
    const unmuteMutation = useUnmuteSiaZoneMutation();

    const handleCopy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`${label} zkopírován do schránky`, 'success');
        } catch {
            showToast('Nepodařilo se zkopírovat do schránky', 'error');
        }
    };

    const handleCreateAccount = () => {
        // Account ID je generovano automaticky na serveru
        createMutation.mutate(
            { ...newAccount, objektId },
            {
                onSuccess: () => {
                    setShowAddDialog(false);
                    setNewAccount({
                        objektId,
                        nazev: '',
                        panelType: 'JA100',
                        encryptionType: 'NONE',
                    });
                },
            }
        );
    };

    const handleRegeneratePassword = (accountId: number) => {
        regenerateMutation.mutate(accountId, {
            onSuccess: (data) => {
                setCurrentPassword(data.password);
                setShowPassword(true);
                setShowPasswordDialog(true);
            },
        });
    };

    const handleMuteZone = () => {
        if (!muteDialogZone) return;
        muteMutation.mutate(
            {
                id: muteDialogZone.zone.id,
                accountId: muteDialogZone.accountId,
                data: { minuty: muteMinutes, aktivovatBypass },
            },
            {
                onSuccess: () => {
                    setMuteDialogZone(null);
                },
            }
        );
    };

    const handleUnmuteZone = (zone: SiaZone, accountId: number) => {
        unmuteMutation.mutate({ id: zone.id, accountId });
    };

    const handleConfirmDeleteAccount = () => {
        if (!deleteAccountConfirm) return;
        deleteMutation.mutate(
            { id: deleteAccountConfirm.id, objektId },
            { onSuccess: () => setDeleteAccountConfirm(null) }
        );
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6">Ústředny EPS/EZS (SIA)</Typography>
                    </Box>
                    {POVOLIT_PRIDANI_SIA && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddDialog(true)}
                            size="small"
                        >
                            Přidat panel
                        </Button>
                    )}
                </Box>
                <Divider sx={{ mb: 2 }} />

                {accounts && accounts.length === 0 ? (
                    <Alert severity="info">
                        {POVOLIT_PRIDANI_SIA
                            ? 'Žádné ústředny EPS/EZS. Kliknutím na „Přidat panel“ přidejte nový SIA účet.'
                            : 'Žádné ústředny EPS/EZS. Zatím jde přidat jen zařízení Shelly.'}
                    </Alert>
                ) : (
                    accounts?.map((account) => (
                        <SiaAccountCard
                            key={account.id}
                            account={account}
                            expanded={expandedAccountId === account.id}
                            onToggleExpand={() =>
                                setExpandedAccountId(
                                    expandedAccountId === account.id ? null : account.id
                                )
                            }
                            onRegeneratePassword={() => handleRegeneratePassword(account.id)}
                            onDelete={() => setDeleteAccountConfirm(account)}
                            onMuteZone={(zone) => setMuteDialogZone({ zone, accountId: account.id })}
                            onUnmuteZone={(zone) => handleUnmuteZone(zone, account.id)}
                            onCopy={handleCopy}
                        />
                    ))
                )}
            </Paper>

            {/* Dialog pro pridani noveho accountu */}
            <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Přidat panel EPS/EZS</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {/* Auto-generovane Account ID */}
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Account ID se vygeneruje automaticky (obsahuje evidenční číslo jednotky, ID objektu
                            a náhodný kód). Po vytvoření accountu zkopírujte celou hodnotu a nastavte ji v panelu
                            (F-Link/MyCOMPANY) v sekci PCO.
                        </Alert>
                        <TextField
                            label="Náhled Account ID"
                            value={isPreviewLoading ? 'Načítám…' : previewData?.accountId || 'Chyba'}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                            }}
                            helperText={TEXT_NAHODNY_KOD}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold',
                                    color: 'text.secondary',
                                },
                            }}
                        />
                        <TextField
                            label="Název"
                            value={newAccount.nazev || ''}
                            onChange={(e) =>
                                setNewAccount({ ...newAccount, nazev: e.target.value })
                            }
                            fullWidth
                            placeholder="např. Hlavní budova"
                        />
                        <TextField
                            select
                            label="Typ panelu"
                            value={newAccount.panelType}
                            onChange={(e) =>
                                setNewAccount({
                                    ...newAccount,
                                    panelType: e.target.value as SiaPanelType,
                                })
                            }
                            fullWidth
                        >
                            {PANEL_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Šifrování"
                            value={newAccount.encryptionType}
                            onChange={(e) =>
                                setNewAccount({
                                    ...newAccount,
                                    encryptionType: e.target.value as SiaEncryptionType,
                                })
                            }
                            fullWidth
                        >
                            {ENCRYPTION_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAddDialog(false)}>Zrušit</Button>
                    <Button
                        onClick={handleCreateAccount}
                        variant="contained"
                        disabled={createMutation.isPending}
                    >
                        {createMutation.isPending ? <CircularProgress size={20} /> : 'Přidat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro zobrazeni hesla */}
            <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
                <DialogTitle>SIA Heslo</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Toto heslo se zobrazí pouze jednou! Zkopírujte si ho.
                    </Alert>
                    <TextField
                        label="Heslo"
                        value={showPassword ? currentPassword || '' : '••••••••••••'}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                    <IconButton onClick={() => handleCopy(currentPassword || '', 'Heslo')}>
                                        <CopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPasswordDialog(false)} variant="contained">
                        Zavřít
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro mute zony */}
            <Dialog open={!!muteDialogZone} onClose={() => setMuteDialogZone(null)}>
                <DialogTitle>Ztlumit zónu</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Zóna: {muteDialogZone?.zone.nazev || `Zóna ${muteDialogZone?.zone.zoneNumber}`}
                    </Typography>
                    <TextField
                        label="Doba ztlumení (minuty)"
                        type="number"
                        value={muteMinutes}
                        onChange={(e) => setMuteMinutes(parseInt(e.target.value) || 30)}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <Alert severity="info">
                        Pokud má zóna přiřazené relé, bypass se automaticky aktivuje.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMuteDialogZone(null)}>Zrušit</Button>
                    <Button
                        onClick={handleMuteZone}
                        variant="contained"
                        color="warning"
                        disabled={muteMutation.isPending}
                    >
                        {muteMutation.isPending ? <CircularProgress size={20} /> : 'Ztlumit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro potvrzeni smazani panelu */}
            <Dialog open={!!deleteAccountConfirm} onClose={() => setDeleteAccountConfirm(null)}>
                <DialogTitle>Smazat panel?</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Panel: <strong>{deleteAccountConfirm?.nazev || `Panel ${deleteAccountConfirm?.accountId}`}</strong>
                    </Typography>
                    <Typography>
                        Opravdu smazat panel? Přijdete o jeho konfiguraci.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteAccountConfirm(null)}>Zrušit</Button>
                    <Button
                        onClick={handleConfirmDeleteAccount}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Smazat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Vnorena komponenta pro jednotlivy SIA account
interface SiaAccountCardProps {
    account: SiaAccount;
    expanded: boolean;
    onToggleExpand: () => void;
    onRegeneratePassword: () => void;
    onDelete: () => void;
    onMuteZone: (zone: SiaZone) => void;
    onUnmuteZone: (zone: SiaZone) => void;
    onCopy: (text: string, label: string) => void;
}

const SiaAccountCard: React.FC<SiaAccountCardProps> = ({
    account,
    expanded,
    onToggleExpand,
    onRegeneratePassword,
    onDelete,
    onMuteZone,
    onUnmuteZone,
    onCopy,
}) => {
    const [showCredentials, setShowCredentials] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const { data: zones } = useSiaZonesByAccountQuery(account.id);
    const { data: events } = useSiaEventsByAccountQuery(account.id, 10);
    const getPasswordMutation = useGetSiaPasswordMutation();
    const regeneratePasswordMutation = useRegenerateSiaPasswordMutation();

    const handleShowCredentials = () => {
        if (!showCredentials) {
            // Načíst heslo při prvním zobrazení
            getPasswordMutation.mutate(account.id, {
                onSuccess: (data) => {
                    setCurrentPassword(data.password);
                    setShowCredentials(true);
                },
            });
        } else {
            setShowCredentials(false);
            setShowPassword(false);
        }
    };

    const handleRegeneratePassword = () => {
        regeneratePasswordMutation.mutate(account.id, {
            onSuccess: (data) => {
                setCurrentPassword(data.password);
                setShowPassword(true);
            },
        });
    };

    const getPanelTypeLabel = (type: SiaPanelType) => {
        return PANEL_TYPES.find((t) => t.value === type)?.label || type;
    };

    const spojeni = stavSpojeniSia(account);

    const isZoneMuted = (zone: SiaZone) => {
        if (!zone.muteDo) return false;
        return new Date(zone.muteDo) > new Date();
    };

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('cs-CZ');
    };

    return (
        <Paper variant="outlined" id={'sia-' + account.id} sx={{ mb: 2, overflow: 'hidden', scrollMarginTop: 80 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={onToggleExpand}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SecurityIcon color={account.aktivni ? 'success' : 'disabled'} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {account.nazev || `Panel ${account.accountId}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Account ID: {account.accountId} | {getPanelTypeLabel(account.panelType)}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Stav spojení podle sdíleného slovníku (spec 1.1) */}
                    <Tooltip title={[
                        spojeni.tooltip,
                        account.statusChangedAt ? `Změna stavu ${formatDateTime(account.statusChangedAt)}` : null,
                    ].filter(Boolean).join(' · ')}>
                        <Chip
                            size="small"
                            data-testid={'sia-spojeni-' + account.id}
                            icon={spojeni.druh === 'online' ? <WifiIcon /> : <WifiOffIcon />}
                            label={spojeni.text}
                            color={spojeni.barva === 'warning' ? 'warning' : 'default'}
                            variant={spojeni.barva === 'warning' ? 'filled' : 'outlined'}
                        />
                    </Tooltip>
                    {!account.aktivni && <Chip size="small" label="Neaktivní" variant="outlined" />}
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
            </Box>

            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ p: 2 }}>
                    {/* Panel status info */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>Poslední kontakt:</strong> {formatDateTime(account.posledniKontakt)}
                        </Typography>
                        {account.lastHeartbeat && (
                            <Typography variant="body2">
                                <strong>Poslední heartbeat:</strong> {formatDateTime(account.lastHeartbeat)}
                            </Typography>
                        )}
                        {account.statusChangedAt && (
                            <Typography variant="body2">
                                <strong>Změna stavu:</strong> {formatDateTime(account.statusChangedAt)}
                            </Typography>
                        )}
                    </Box>

                    {/* Credentials section */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                            Přihlašovací údaje pro panel
                        </Typography>

                        {/* Account ID */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                                <strong>Account ID:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 0.5 }}>
                                {account.accountId}
                            </Typography>
                            <Tooltip title="Zkopírovat Account ID">
                                <IconButton size="small" onClick={() => onCopy(account.accountId, 'Account ID')}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Password */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                                <strong>Heslo:</strong>
                            </Typography>
                            {showCredentials && currentPassword ? (
                                <>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 0.5 }}>
                                        {showPassword ? currentPassword : '••••••••••••'}
                                    </Typography>
                                    <Tooltip title={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}>
                                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Zkopírovat heslo">
                                        <IconButton size="small" onClick={() => onCopy(currentPassword, 'Heslo')}>
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={handleShowCredentials}
                                    disabled={getPasswordMutation.isPending}
                                >
                                    {getPasswordMutation.isPending ? 'Načítám…' : 'Zobrazit heslo'}
                                </Button>
                            )}
                        </Box>

                        {/* Regenerate password */}
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={handleRegeneratePassword}
                                disabled={regeneratePasswordMutation.isPending}
                            >
                                {regeneratePasswordMutation.isPending ? 'Generuji…' : 'Vygenerovat nové heslo'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                        >
                            Smazat panel
                        </Button>
                    </Box>

                    {/* Zony */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Zóny ({zones?.length || 0})
                    </Typography>
                    {zones && zones.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Číslo</TableCell>
                                        <TableCell>Název</TableCell>
                                        <TableCell>Typ</TableCell>
                                        <TableCell>Stav</TableCell>
                                        <TableCell>Akce</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {zones.map((zone) => (
                                        <TableRow key={zone.id}>
                                            <TableCell>{zone.zoneNumber}</TableCell>
                                            <TableCell>{zone.nazev || '-'}</TableCell>
                                            <TableCell>{zone.typ}</TableCell>
                                            <TableCell>
                                                {isZoneMuted(zone) ? (
                                                    <Chip
                                                        size="small"
                                                        label={`Ztlumeno do ${formatDateTime(zone.muteDo)}`}
                                                        color="warning"
                                                    />
                                                ) : (
                                                    <Chip size="small" label="Aktivní" variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isZoneMuted(zone) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onUnmuteZone(zone)}
                                                        title="Zrušit ztlumení"
                                                    >
                                                        <UnmuteIcon fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onMuteZone(zone)}
                                                        title="Ztlumit"
                                                    >
                                                        <MuteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Žádné zóny. Zóny se vytvoří automaticky při příjmu první události.
                        </Alert>
                    )}

                    {/* Posledni udalosti */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Poslední události
                    </Typography>
                    {events && events.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Čas</TableCell>
                                        <TableCell>Kód</TableCell>
                                        <TableCell>Zóna</TableCell>
                                        <TableCell>Stav</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.slice(0, 5).map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell>{formatDateTime(event.cas)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={`${event.siaQualifier}${event.siaCode}`}
                                                />
                                            </TableCell>
                                            <TableCell>{event.zoneNumber || '-'}</TableCell>
                                            <TableCell>
                                                {event.poplachVytvoren ? (
                                                    <Chip size="small" label="Poplach" color="error" />
                                                ) : event.mutovano ? (
                                                    <Chip size="small" label="Ztlumeno" color="warning" />
                                                ) : (
                                                    <Chip size="small" label="OK" variant="outlined" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">Zatím žádné události.</Alert>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default EpsEzsSection;
